import React, { useState } from 'react';
import EmailVerificationStep from './EmailVerificationStep';
import SignupModal from './SignupModal';
import { useNotifications } from '../hooks/useNotifications';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useSignUp } from '../services/userService';
import { linkLoginIdToEmail } from '../services/passwordResetService';
import googleAnalytics from '../services/googleAnalyticsService';

export interface PendingSignupData {
  loginId: string;
  email: string;
  loginPwd: string;
  loginPwd_confirm: string;
  code: string;
}

interface SignupFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  onSignupSuccess?: (userData: { loginId: string; name: string; email?: string }) => void;
  code?: string;
}

type SignupStep = 'signup-modal' | 'verification';

const SignupFlow: React.FC<SignupFlowProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  onSignupSuccess,
  code = '',
}) => {
  const { success, info } = useNotifications();
  useBodyScrollLock(isOpen);

  const [currentStep, setCurrentStep] = useState<SignupStep>('signup-modal');
  const [pendingSignupData, setPendingSignupData] = useState<PendingSignupData | null>(null);

  const signUpMutation = useSignUp();

  const handleSendVerificationCode = (formData: PendingSignupData) => {
    setPendingSignupData(formData);
    setCurrentStep('verification');
  };

  const handleEmailVerified = async (email: string) => {
    if (!pendingSignupData) return;
    info('Creating account…', 'Please wait.');
    const signupData = {
      ...pendingSignupData,
      email,
    };
    const response = await signUpMutation.mutateAsync(signupData);
    if (response.status === 1) {
      throw new Error(response.message?.warning || 'Signup failed');
    }
    const emailToLink = email;
    if (emailToLink && pendingSignupData.loginId) {
      try {
        await linkLoginIdToEmail(emailToLink, pendingSignupData.loginId);
      } catch {
        // non-blocking
      }
    }
    success('Signup complete', 'Your account has been created.');
    if (onSignupSuccess) {
      onSignupSuccess({
        loginId: pendingSignupData.loginId,
        name: pendingSignupData.loginId,
        email,
      });
    }
    googleAnalytics.trackSignup('email');
    resetFlow();
  };

  const handleSignupSuccess = (userData: { loginId: string; name: string; email?: string }) => {
    if (onSignupSuccess) onSignupSuccess(userData);
    resetFlow();
  };

  const handleClose = () => {
    resetFlow();
    onClose();
  };

  const handleSwitchToLogin = () => {
    resetFlow();
    onSwitchToLogin();
  };

  const handleBackToSignupForm = () => {
    setCurrentStep('signup-modal');
    setPendingSignupData(null);
  };

  const resetFlow = () => {
    setCurrentStep('signup-modal');
    setPendingSignupData(null);
  };

  if (!isOpen) return null;

  return (
    <>
      {currentStep === 'signup-modal' && (
        <SignupModal
          isOpen={true}
          onClose={handleClose}
          onSwitchToLogin={handleSwitchToLogin}
          onSignupSuccess={handleSignupSuccess}
          code={code}
          verifiedEmail={undefined}
          onSendVerificationCode={handleSendVerificationCode}
        />
      )}

      {currentStep === 'verification' && pendingSignupData && (
        <EmailVerificationStep
          initialEmail={pendingSignupData.email}
          onEmailVerified={handleEmailVerified}
          onSwitchToLogin={handleSwitchToLogin}
          onClose={handleClose}
          onBack={handleBackToSignupForm}
        />
      )}
    </>
  );
};

export default SignupFlow;
